import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { confirmResetPassword, confirmSignUp, confirmUserAttribute } from 'aws-amplify/auth'
import queryString from 'query-string'
import MainNav from '../components/MainNav'
import FormPage from '../components/FormPage'
import FormContainer from '../components/FormContainer'
import FormLabel from '../components/FormLabel'
import Button from '../components/Button'
import ErrorMessage from '../components/ErrorMessage'
import Toaster from '../components/Toaster'
import styled from 'styled-components/macro'
import { Link } from 'react-router-dom'
import FormValidator, { ERROR_CODE } from '../components/FormValidator'
import Footer from '../components/Footer'
import { useStateValue } from '../state'
import { get } from 'lodash-es'
import { CONFIRMATION_TYPES } from '../constants/confirmationConstants'

const Description = styled.p`
  font-size: ${props => props.theme.fontSizeSmall};
  margin: 0 0 20px;
`

const errorMessages = {
  [ERROR_CODE.NotAuthorized]: 'Tunnusta ei voida vahvistaa.',
  [ERROR_CODE.CodeMismatch]: 'Väärä vahvistuskoodi.',
  [ERROR_CODE.ExpiredCode]: 'Vahvistuskoodi on vanhentunut.',
  [ERROR_CODE.InvalidParameter]:
    'Salasanan on oltava vähintään 10 merkkiä pitkä.'
}

const ConfirmAccount = ({ history }) => {
  const [{ user }] = useStateValue()
  const [username, setUsername] = useState(get(user, 'attributes.email', ''))
  const [confirmationCode, setConfirmationCode] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [errorCodes, setErrorCodes] = useState([])
  const [errorPayload, setErrorPayload] = useState({})
  const query = queryString.parse(history.location.search)
  const confirmationType = query.confirmationType || CONFIRMATION_TYPES.ACCOUNT

  const titleMap = {
    [CONFIRMATION_TYPES.PASSWORD]: 'Vaihda salasana',
    [CONFIRMATION_TYPES.EMAIL]: 'Vahvista sähköpostin vaihto',
    [CONFIRMATION_TYPES.ACCOUNT]: 'Vahvista tili'
  }

  const successMsgMap = {
    [CONFIRMATION_TYPES.PASSWORD]: 'Salasanan vaihto onnistui!',
    [CONFIRMATION_TYPES.EMAIL]: 'Sähköpostin vaihto vahvistettu!',
    [CONFIRMATION_TYPES.ACCOUNT]: 'Tilisi on nyt vahvistettu ja voit kirjautua sisään.'
  }

  const title = titleMap[confirmationType] || titleMap[CONFIRMATION_TYPES.ACCOUNT]
  const successMsg = successMsgMap[confirmationType] || titleMap[CONFIRMATION_TYPES.ACCOUNT]

  useEffect(() => {
    document.title = `Business Joensuu - ${title}`
  }, [title])

  const confirmAccount = async () => {
    try {
      setErrorCodes([])
      const operationFn = (() => {
        switch (confirmationType) {
          case CONFIRMATION_TYPES.PASSWORD:
            return confirmResetPassword({
              username,
              confirmationCode,
              newPassword
            });
          case CONFIRMATION_TYPES.EMAIL:
            return confirmUserAttribute({
              userAttributeKey: 'email',
              confirmationCode
            });
          case CONFIRMATION_TYPES.ACCOUNT:
          default:
            return confirmSignUp({
              username,
              confirmationCode
            });
        }
      })()

      await operationFn
      setShowToast(true)
    } catch (err) {
      setErrorCodes([err.code])
      console.error(err)
    }
  }

  return (
    <>
      <FormPage>
        <input type="hidden" value="empty" />
        <MainNav history={history} />
        <FormContainer>
          <h1>{title}</h1>
          {showToast
            ? (
              <Toaster message={successMsg}>
                { confirmationType !== CONFIRMATION_TYPES.EMAIL &&
                  <Link to="/login">
                    <Button isPrimary isFullWidth>
                      Kirjaudu sisään
                  </Button>
                  </Link>
                }
              </Toaster>
            )
            : (
              <FormValidator
                messages={errorMessages}
                codes={errorCodes}
                onChange={setErrorPayload}
              >
                <Description>
                  Olemme lähettäneet vahvistuskoodin sähköpostiisi.
              </Description>
                <FormLabel hasErrorMessage>
                  Sähköposti
                <input
                    type="email"
                    value={username}
                    onChange={event => setUsername(event.target.value)}
                  />
                </FormLabel>
                <ErrorMessage
                  codes={[ERROR_CODE.NotAuthorized, ERROR_CODE.UserNotFound]}
                  payload={errorPayload}
                />
                <FormLabel hasErrorMessage>
                  Vahvistuskoodi
                <input
                    autoComplete="email"
                    onChange={event => setConfirmationCode(event.target.value)}
                  />
                </FormLabel>
                <ErrorMessage
                  codes={[ERROR_CODE.CodeMismatch, ERROR_CODE.ExpiredCode]}
                  payload={errorPayload}
                />
                {confirmationType === CONFIRMATION_TYPES.PASSWORD
                  ? (
                    <FormLabel hasErrorMessage>
                      Uusi salasana
                      <input
                        type="password"
                        autoComplete="new-password"
                        onChange={event => setNewPassword(event.target.value)}
                      />
                    </FormLabel>
                  )
                  : null}
                <ErrorMessage
                  codes={[
                    ERROR_CODE.InvalidParameter,
                    ERROR_CODE.InvalidPassword
                  ]}
                  payload={errorPayload}
                />
                <Button isPrimary isFullWidth onClick={confirmAccount}>
                  {title}
                </Button>
              </FormValidator>
            )}
        </FormContainer>
      </FormPage>
      <Footer />
    </>
  )
}

ConfirmAccount.propTypes = {
  history: PropTypes.object
}

export default ConfirmAccount
